/**
 * Vessel-cluster grid decisions — the maths and markup behind the count
 * bubbles, extracted pure from `VoyageMapController` (phase 1,
 * REFACTOR-PLAN.md §7). What stayed behind is everything Leaflet: projection,
 * marker pooling, layer membership. Pure model file: no `@angular/*`, no
 * `leaflet` (rule R1); the caller wraps `clusterBubbleSpec` in `L.divIcon`.
 */

/**
 * Grid cell for clustering, in px. Deliberately smaller than a label's cell:
 * a bubble only has to stop ARROWS overlapping, and arrows are 24–50px wide,
 * where a name is ~110px.
 */
export const CLUSTER_CELL = 60;

/**
 * Below this, the cell draws its arrows instead of a bubble.
 *
 * Three, not two: a "2" bubble replaces two hulls you could already tell apart
 * with a count you then have to click to resolve — strictly less information in
 * the same space. It also keeps a pair of vessels passing each other visible as
 * two vessels passing each other, which is the reading the chart is for.
 */
export const CLUSTER_MIN = 3;

/** A projected point carrying whatever the caller clusters (markers, ids…). */
export interface ClusterPoint<T> {
  x: number;
  y: number;
  item: T;
}

/** One grid cell's members plus their centroid, in the same projected space. */
export interface ClusterCell<T> {
  members: T[];
  /**
   * The centroid of the members, not the centre of the cell: a bubble parked
   * on a grid intersection sits in open water beside the traffic it counts.
   */
  centroidX: number;
  centroidY: number;
}

/**
 * Buckets projected points into `cellPx` grid cells and returns every cell
 * holding at least `min` members, in first-seen order (which is what keeps the
 * caller's bubble pool stable from frame to frame during playback).
 */
export function bucketIntoCells<T>(
  points: Array<ClusterPoint<T>>,
  cellPx: number = CLUSTER_CELL,
  min: number = CLUSTER_MIN,
): Array<ClusterCell<T>> {
  const cells = new Map<string, { members: T[]; x: number; y: number }>();
  for (const p of points) {
    const cx = Math.floor(p.x / cellPx);
    const cy = Math.floor(p.y / cellPx);
    const key = `${cx}:${cy}`;
    const cell = cells.get(key);
    if (cell) {
      cell.members.push(p.item);
      cell.x += p.x;
      cell.y += p.y;
    } else {
      cells.set(key, { members: [p.item], x: p.x, y: p.y });
    }
  }
  const out: Array<ClusterCell<T>> = [];
  cells.forEach((cell) => {
    if (cell.members.length < min) return;
    out.push({
      members: cell.members,
      centroidX: cell.x / cell.members.length,
      centroidY: cell.y / cell.members.length,
    });
  });
  return out;
}

/** Everything `L.divIcon` needs for a count bubble — built pure. */
export interface ClusterBubbleSpec {
  className: string;
  html: string;
  iconSize: [number, number];
  iconAnchor: [number, number];
}

/**
 * The bubble glyph. Four size tiers rather than a continuous scale: the count
 * is written on it, so the size only has to say "more than that one", and a
 * smooth area ramp makes every bubble a slightly different size for no reading.
 */
export function clusterBubbleSpec(count: number): ClusterBubbleSpec {
  const tier = count < 10 ? 'sm' : count < 50 ? 'md' : count < 200 ? 'lg' : 'xl';
  const size = tier === 'sm' ? 30 : tier === 'md' ? 36 : tier === 'lg' ? 42 : 48;
  // Four digits do not fit a 48px circle, and past a thousand the exact number
  // is not the point — the bubble is telling you to zoom in either way.
  const text = count >= 1000 ? `${(count / 1000).toFixed(1)}k` : String(count);
  return {
    className: `vr-cluster vr-cluster--${tier}`,
    html: `<span>${text}</span>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  };
}
